import DossierSteps from "../DossierSteps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import MplusApi from "Services/Mplus";
import DossierDetails from "../DossierDetails";
import {
  FINANCING_STATUS,
  FREQUENCY_LABELS,
  apiErrorCode,
  apiErrorMessage,
  fieldErrors,
  financingProduct,
  financingReached,
  formatAmount,
  formatDate,
  normalizePeriods,
  productId,
  repaymentCount,
  toInputDate,
  todayIso,
  unwrap,
  unwrapList,
  useHasRole,
} from "../mplusUtils";
import useStyles from "./style";

const CHART = { left: 42, right: 420, top: 15, bottom: 123 };
const MAX_WEEKS = 60;

// Construit les courbes capital / intérêts à partir de l'échéancier.
const buildChart = (periods) => {
  if (!periods.length) return null;
  const max = Math.max(...periods.map((period) => Math.max(period.principal, period.interest)), 1);
  const step = periods.length > 1 ? (CHART.right - CHART.left) / (periods.length - 1) : 0;
  const point = (value, index) => [CHART.left + index * step, CHART.bottom - (value / max) * (CHART.bottom - CHART.top)];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
    y: CHART.bottom - ratio * (CHART.bottom - CHART.top),
    label: max * ratio >= 1000 ? `${Math.round((max * ratio) / 1000)}k` : Math.round(max * ratio),
  }));
  const labelEvery = Math.max(1, Math.ceil(periods.length / 7));
  return {
    capital: periods.map((period, index) => point(period.principal, index)),
    interest: periods.map((period, index) => point(period.interest, index)),
    ticks,
    xLabels: periods
      .map((period, index) => ({ x: CHART.left + index * step, label: period.period }))
      .filter((item, index) => index % labelEvery === 0 || index === periods.length - 1),
  };
};

const FINERACT_STEPS = [
  { action: "setup-fineract", label: "Client et portefeuille Fineract prêts" },
  { action: "create-loan", label: "Création du prêt", reached: "LOAN_CREATED" },
  { action: "approve-loan", label: "Approbation du prêt", reached: "LOAN_APPROVED" },
];

const DELIVERY_EDITABLE = ["SUBMITTED", "LOAN_CREATED", "LOAN_APPROVED"];

// Conditions du dossier → valeurs du formulaire.
const formFrom = (financing, product, defaultMonths) => ({
  number_of_repayments: String(financing?.number_of_repayments ?? defaultMonths ?? ""),
  annual_interest_rate: String(financing?.annual_interest_rate ?? product?.annual_interest_rate ?? ""),
  down_payment: String(financing?.down_payment ?? 0),
  fees: String(financing?.fees ?? 0),
  repayment_frequency: financing?.repayment_frequency || "MONTHLY",
  first_repayment_date: toInputDate(financing?.first_repayment_date),
});

// Mêmes paramètres pour la simulation, la création et la modification du dossier.
const paramsFrom = (form) => {
  const params = { repayment_frequency: form.repayment_frequency };
  ["number_of_repayments", "annual_interest_rate", "down_payment", "fees"].forEach((key) => {
    if (form[key] !== "" && form[key] != null) params[key] = Number(form[key]);
  });
  if (form.first_repayment_date) params.first_repayment_date = form.first_repayment_date;
  return params;
};

const validate = (form, price) => {
  const errors = {};
  const months = Number(form.number_of_repayments);
  if (!months || months < 1) errors.number_of_repayments = "Nombre d'échéances requis.";
  else if (form.repayment_frequency === "WEEKLY" && months > MAX_WEEKS) errors.number_of_repayments = `${MAX_WEEKS} semaines au maximum.`;
  const down = Number(form.down_payment || 0);
  if (down < 0) errors.down_payment = "L'apport ne peut pas être négatif.";
  else if (price != null && down >= Number(price)) errors.down_payment = "L'apport doit être inférieur au prix du véhicule.";
  if (Number(form.fees || 0) < 0) errors.fees = "Les frais ne peuvent pas être négatifs.";
  if (form.annual_interest_rate !== "" && Number(form.annual_interest_rate) < 0) errors.annual_interest_rate = "Taux invalide.";
  if (form.first_repayment_date && form.first_repayment_date <= todayIso()) errors.first_repayment_date = "La date doit être dans le futur.";
  return errors;
};

const ProgrammeMPlusFinancing = ({ application, financing, creation, eligibility, photoUrl, notify, onBack, onReload, onNext, onCreated }) => {
  const classes = useStyles();
  const hasRole = useHasRole();
  const product = financingProduct(financing, application);
  const status = financing?.status || "SUBMITTED";
  const statusInfo = creation ? { label: "Dossier à ouvrir", tone: "pending" } : FINANCING_STATUS[status] || FINANCING_STATUS.SUBMITTED;
  const catalogId = productId(financing, application) || creation?.productCatalogId;
  const knownPrice = financing?.cash_price ?? product?.unit_cost ?? application?.vehiclePrice
    ?? (financing?.principal_amount != null ? Number(financing.principal_amount) + Number(financing.down_payment || 0) : null);
  const [manualPrice, setManualPrice] = useState("");
  const price = knownPrice ?? (manualPrice === "" ? null : Number(manualPrice));
  const validPrice = price != null && Number.isFinite(Number(price)) && Number(price) > 0;
  const missingSimulationAmount = !catalogId && !validPrice;
  const isFinance = hasRole("FINANCE");

  const [editing, setEditing] = useState(Boolean(creation));
  const [form, setForm] = useState(() => formFrom(financing, product, creation?.defaultMonths ?? repaymentCount(financing, application)));
  const [serverErrors, setServerErrors] = useState({});
  const [periods, setPeriods] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [scheduleSource, setScheduleSource] = useState("");
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [running, setRunning] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [locations, setLocations] = useState([]);
  const [plannedDate, setPlannedDate] = useState(toInputDate(financing?.planned_delivery_date));
  const [plannedLocation, setPlannedLocation] = useState(financing?.delivery_location_id || "");

  // Resynchronise le formulaire quand le dossier est rechargé.
  useEffect(() => {
    if (creation) return;
    setForm(formFrom(financing, product, repaymentCount(financing, application)));
    setPlannedDate(toInputDate(financing?.planned_delivery_date));
    setPlannedLocation(financing?.delivery_location_id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financing]);

  useEffect(() => {
    MplusApi.getLocations()
      .then((response) => setLocations(unwrapList(response).filter((location) => !location.is_virtual && location.is_active !== false)))
      .catch(() => setLocations([]));
  }, []);

  const clientErrors = useMemo(() => (editing ? validate(form, price) : {}), [editing, form, price]);
  const errors = { ...serverErrors, ...clientErrors };
  const params = useMemo(() => paramsFrom(form), [form]);
  const loanCreated = !creation && financingReached(status, "LOAN_CREATED");
  const loanApproved = !creation && financingReached(status, "LOAN_APPROVED");
  const cancelled = status === "CANCELLED" && !creation;
  const weekly = form.repayment_frequency === "WEEKLY";

  // Échéancier : réel une fois le prêt créé, sinon simulation avec exactement les paramètres du dossier.
  const loadSchedule = useCallback(async (signal) => {
    if (loanCreated) {
      const data = unwrap(await MplusApi.getFinancingSchedule(financing.id));
      if (signal.cancelled) return;
      setPeriods(normalizePeriods(data?.periods || [], financing.principal_amount));
      setSimulation(null);
      setScheduleSource("Échéancier réel (Fineract)");
      return;
    }
    const query = { ...params };
    if (missingSimulationAmount) {
      setPeriods([]);
      setSimulation(null);
      setScheduleSource("");
      return;
    }
    if (catalogId) query.product_catalog_id = catalogId;
    // L'API applique ensuite l'apport : principal correspond ici au prix comptant.
    else query.principal = Number(price);
    const data = unwrap(await MplusApi.simulateSchedule(query));
    if (signal.cancelled) return;
    setPeriods(normalizePeriods(data?.periods || [], data?.principal));
    setSimulation(data || null);
    setScheduleSource(data?.dailyCollection ? `Simulation · prélèvement journalier ${formatAmount(data.dailyCollection)}` : "Simulation");
  }, [catalogId, financing, loanCreated, missingSimulationAmount, params, price]);

  useEffect(() => {
    if (!financing && !creation) return undefined;
    if (Object.keys(clientErrors).length) return undefined;
    const signal = { cancelled: false };
    // Debounce de 400 ms pendant la saisie.
    const timer = setTimeout(() => {
      setLoadingSchedule(true);
      loadSchedule(signal)
        .catch((error) => {
          if (signal.cancelled) return;
          setPeriods([]);
          setServerErrors(fieldErrors(error));
          notify(apiErrorMessage(error, "Impossible de calculer l'échéancier."), "error");
        })
        .finally(() => { if (!signal.cancelled) setLoadingSchedule(false); });
    }, editing ? 400 : 0);
    return () => { signal.cancelled = true; clearTimeout(timer); };
  }, [clientErrors, creation, editing, financing, loadSchedule, notify]);

  const setField = (key) => (event) => {
    const { value } = event.target;
    setServerErrors((previous) => ({ ...previous, [key]: undefined }));
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const totals = useMemo(() => periods.reduce((result, period) => ({
    interest: result.interest + period.interest,
    total: result.total + period.total,
  }), { interest: 0, total: 0 }), [periods]);
  const chart = useMemo(() => buildChart(periods), [periods]);

  // Montants affichés en direct : prix − apport = montant financé.
  const downPayment = editing ? Number(form.down_payment || 0) : Number(financing?.down_payment ?? simulation?.downPayment ?? 0);
  const fees = editing ? Number(form.fees || 0) : (financing?.fees ?? simulation?.fees ?? null);
  const principal = editing
    ? (price == null ? null : Math.max(Number(price) - downPayment, 0))
    : financing?.principal_amount ?? simulation?.principal ?? null;
  const count = Number(form.number_of_repayments) || null;
  const rate = form.annual_interest_rate === "" ? null : form.annual_interest_rate;
  const firstDueDate = form.first_repayment_date || periods[0]?.dueDate;

  // Capacité de remboursement : contrôle DEBT_RATIO de l'éligibilité (section 5).
  const debtCheck = (eligibility?.checks || []).find((check) => check.code === "DEBT_RATIO");
  const monthlyIncome = eligibility?.monthly_income ?? application?.monthlyIncome;

  const handleError = (error, fallback) => {
    setServerErrors(fieldErrors(error));
    notify(apiErrorMessage(error, fallback), "error");
    if (apiErrorCode(error) === "MPLUS_FINANCING_LOCKED") {
      setEditing(false);
      onReload();
    }
  };

  const handleCreate = async () => {
    if (missingSimulationAmount || Object.keys(clientErrors).length) {
      notify("Renseignez un prix comptant positif et corrigez les conditions du financement.", "error");
      return;
    }
    if (creation.notEligible && !creation.overrideReason) {
      notify("Demandeur non éligible : renseignez le motif de dérogation à l'étape 1.", "error");
      return;
    }
    setRunning("Ouverture du dossier");
    try {
      const created = unwrap(await MplusApi.createFinancing({
        vehicle_lease_purchase_id: creation.leaseId,
        product_catalog_id: catalogId || undefined,
        principal_amount: !catalogId ? Number(price) : undefined,
        asset_id: creation.assetId || undefined,
        eligibility_override_reason: creation.overrideReason || undefined,
        ...params,
      }));
      notify("Dossier ouvert.", "success");
      setEditing(false);
      await onCreated(created);
    } catch (error) {
      if (apiErrorCode(error) === "MPLUS_FINANCING_EXISTS") {
        notify(apiErrorMessage(error, "Un dossier existe déjà."), "info");
        await onCreated(null);
        return;
      }
      handleError(error, "Impossible d'ouvrir le dossier.");
    } finally {
      setRunning("");
    }
  };

  const handleSaveConditions = async () => {
    setRunning("Enregistrement");
    try {
      await MplusApi.updateFinancing(financing.id, params);
      notify("Conditions du dossier enregistrées.", "success");
      setEditing(false);
      await onReload();
    } catch (error) {
      handleError(error, "Impossible d'enregistrer les conditions.");
    } finally {
      setRunning("");
    }
  };

  const handleSaveDelivery = async () => {
    setRunning("Livraison prévue");
    try {
      await MplusApi.updateFinancing(financing.id, {
        planned_delivery_date: plannedDate || null,
        delivery_location_id: plannedLocation || null,
      });
      notify("Livraison prévue enregistrée.", "success");
      await onReload();
    } catch (error) {
      handleError(error, "Impossible d'enregistrer la livraison prévue.");
    } finally {
      setRunning("");
    }
  };

  // Enchaîne les étapes Fineract restantes : client/portefeuille → prêt → approbation.
  const handleValidate = async () => {
    if (loanApproved) {
      onNext();
      return;
    }
    let current = "mise en place du financement";
    try {
      for (const step of FINERACT_STEPS) {
        const done = step.reached ? financingReached(status, step.reached) : Boolean(financing.setup_done);
        if (done) continue;
        current = step.label;
        setRunning(step.label);
        try {
          await MplusApi.runFinancingAction(financing.id, step.action);
        } catch (error) {
          if (step.action === "setup-fineract" && error?.response?.status === 409) continue;
          throw error;
        }
      }
      notify("Financement mis en place et prêt approuvé.", "success");
      await onReload();
      onNext();
    } catch (error) {
      notify(apiErrorMessage(error, `Échec : ${current}.`), "error");
      onReload();
    } finally {
      setRunning("");
    }
  };

  const handleCancel = async () => {
    setRunning("Annulation");
    try {
      await MplusApi.runFinancingAction(financing.id, "cancel", { reason: cancelReason.trim() });
      notify("Dossier annulé.", "success");
      setCancelOpen(false);
      onReload();
    } catch (error) {
      notify(apiErrorMessage(error, "Impossible d'annuler le dossier."), "error");
    } finally {
      setRunning("");
    }
  };

  const numberField = (key, label, extra = {}) => (
    <Box className={classes.field} key={key}>
      <span>{label}</span>
      <TextField
        size="small"
        type="number"
        className={classes.selectField}
        value={form[key]}
        error={Boolean(errors[key])}
        helperText={errors[key] || " "}
        inputProps={{ min: 0, ...extra }}
        onChange={setField(key)}
      />
    </Box>
  );

  const readField = (label, value, icon) => (
    <Box className={classes.field} key={label}>
      <span>{label}</span>
      <Box className={classes.inputWrap}><i>{icon}</i><b className={classes.readValue}>{value}</b></Box>
    </Box>
  );

  const visiblePeriods = showAll ? periods : periods.slice(0, 3);
  const countLabel = weekly ? "Nombre de semaines" : "Nombre de mensualités";
  const installmentLabel = weekly ? "Échéance hebdomadaire" : "Mensualité estimée";

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Box className={classes.titleArea}>
          <Box className={classes.breadcrumb}>Gestion des dossiers <span>›</span> {financing?.reference || application?.reference || "--"}</Box>
          <Box className={classes.headingLine}>
            <Typography className={classes.pageTitle}>Demande {application?.reference || "--"}</Typography>
            <span className={statusInfo.tone === "rejected" ? classes.cancelledBadge : classes.configBadge}>{statusInfo.label}</span>
          </Box>
        </Box>
        <DossierSteps activeStep={2} />
      </Box>

      <Box className={classes.mainGrid}>
        <Box className={classes.leftColumn}>
          <Box className={classes.card}>
            <Typography className={classes.sectionTitle}><PaymentsOutlinedIcon />Paramètres du financement</Typography>

            {/* Montants calculés en direct : prix comptant − apport = montant financé. */}
            <Box className={classes.amountStrip}>
              <Box><small>Prix comptant</small><b>{formatAmount(price)}</b></Box>
              <Box><small>Apport</small><b>{formatAmount(downPayment)}</b></Box>
              <Box className={classes.amountHighlight}><small>Montant financé</small><b>{formatAmount(principal)}</b></Box>
              <Box><small>Frais de dossier</small><b>{formatAmount(fees)}</b></Box>
            </Box>

            {editing ? (
              <Box className={classes.formGrid}>
                {creation && !catalogId && knownPrice == null && (
                  <Box className={classes.field}>
                    <span>Prix comptant (XAF)</span>
                    <TextField
                      size="small"
                      type="number"
                      aria-label="Prix comptant (XAF)"
                      value={manualPrice}
                      inputProps={{ min: 1, step: "any", "aria-label": "Prix comptant (XAF)" }}
                      error={manualPrice !== "" && !validPrice}
                      helperText="Aucun modèle de catalogue associé : renseignez le prix du véhicule."
                      onChange={(event) => setManualPrice(event.target.value)}
                    />
                  </Box>
                )}
                <Box className={classes.field}>
                  <span>Fréquence</span>
                  <TextField select size="small" className={classes.selectField} value={form.repayment_frequency} error={Boolean(errors.repayment_frequency)} helperText={errors.repayment_frequency || " "} onChange={setField("repayment_frequency")}>
                    {Object.entries(FREQUENCY_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </TextField>
                </Box>
                {numberField("number_of_repayments", countLabel, { min: 1, max: weekly ? MAX_WEEKS : undefined })}
                {numberField("annual_interest_rate", "Taux annuel (%)", { step: 0.1 })}
                {numberField("down_payment", "Apport (XAF)")}
                {numberField("fees", "Frais de dossier (XAF)")}
                <Box className={classes.field}>
                  <span>Date du 1er prélèvement</span>
                  <TextField
                    size="small"
                    type="date"
                    className={classes.selectField}
                    value={form.first_repayment_date}
                    error={Boolean(errors.first_repayment_date)}
                    helperText={errors.first_repayment_date || "Facultatif"}
                    inputProps={{ min: todayIso() }}
                    onChange={setField("first_repayment_date")}
                  />
                </Box>
              </Box>
            ) : (
              <Box className={classes.formGrid}>
                {readField("Fréquence", FREQUENCY_LABELS[financing?.repayment_frequency || "MONTHLY"], <CalendarMonthRoundedIcon />)}
                {readField(countLabel, count || "--", <CalendarMonthRoundedIcon />)}
                {readField("Taux annuel", rate == null ? "--" : `${rate} %`, <PaymentsOutlinedIcon />)}
                {readField(installmentLabel, formatAmount(periods[0]?.total), <PaymentsOutlinedIcon />)}
                {readField("Date du 1er prélèvement", formatDate(firstDueDate), <CalendarMonthRoundedIcon />)}
              </Box>
            )}
            <Typography className={classes.frozenHint}>
              {creation
                ? "La simulation utilise exactement les paramètres qui seront envoyés à l'ouverture du dossier."
                : status === "SUBMITTED"
                  ? "Les conditions restent modifiables tant que le prêt n'est pas créé."
                  : "Conditions figées : le prêt est créé dans Fineract."}
            </Typography>

            <Box className={classes.simulation}>
              <Typography className={classes.simTitle}><ShowChartRoundedIcon />Simulation du remboursement</Typography>
              {missingSimulationAmount && !loanCreated ? (
                <Typography className={classes.helpText}>Renseignez le prix comptant du véhicule pour calculer l'échéancier.</Typography>
              ) : loadingSchedule ? (
                <Box className={classes.loaderBox}><CircularProgress size={24} /></Box>
              ) : (
                <>
                  <Box className={classes.summaryGrid}>
                    <Box className={classes.summaryCard}><span className={classes.moneyIcon}>₣</span><small>{installmentLabel}</small><b>{formatAmount(periods[0]?.total)}</b></Box>
                    <Box className={classes.summaryCard}><span className={classes.moneyIcon}>₣</span><small>Coût total du crédit</small><b>{periods.length ? formatAmount(totals.interest) : "--"}</b></Box>
                    <Box className={classes.summaryCard}><span className={classes.moneyIcon}>₣</span><small>Total à rembourser</small><b>{periods.length ? formatAmount(totals.total) : "--"}</b></Box>
                  </Box>
                  <Box className={classes.chartDetails}>
                    <Box className={classes.chartCard}>
                      <Typography className={classes.chartTitle}>Répartition des échéances</Typography>
                      <Box className={classes.legend}><span><i />Capital</span><span><i />Intérêts</span></Box>
                      {chart ? (
                        <svg className={classes.chart} viewBox="0 0 430 145" role="img" aria-label="Répartition du capital et des intérêts par échéance">
                          <g className={classes.gridLines}>{chart.ticks.map((tick) => <path key={tick.y} d={`M${CHART.left} ${tick.y}H${CHART.right}`} />)}</g>
                          <polyline className={classes.capitalLine} points={chart.capital.map((p) => p.join(",")).join(" ")} />
                          <polyline className={classes.interestLine} points={chart.interest.map((p) => p.join(",")).join(" ")} />
                          <g className={classes.axisText}>
                            {chart.ticks.map((tick) => <text key={tick.y} x="3" y={tick.y + 3}>{tick.label}</text>)}
                            {chart.xLabels.map((item) => <text key={item.x} x={item.x - 3} y="141">{item.label}</text>)}
                          </g>
                        </svg>
                      ) : <Typography className={classes.helpText}>{Object.keys(clientErrors).length ? "Corrigez les champs pour lancer la simulation." : "Aucune échéance à afficher."}</Typography>}
                    </Box>
                    <Box className={classes.creditDetails}>
                      <Typography className={classes.chartTitle}>Détails du crédit</Typography>
                      <Box><span>Capital emprunté</span><b>{formatAmount(principal)}</b></Box>
                      <Box><span>Taux annuel</span><b>{rate == null ? "--" : `${rate} %`}</b></Box>
                      <Box><span>Durée</span><b>{count ? `${count} ${weekly ? "semaines" : "mois"}` : "--"}</b></Box>
                      <Box><span>Fréquence</span><b>{FREQUENCY_LABELS[form.repayment_frequency] || "--"}</b></Box>
                    </Box>
                  </Box>
                  {scheduleSource && <Typography className={classes.frozenHint}>{scheduleSource}</Typography>}
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box className={classes.rightColumn}>
          <Box className={classes.card}>
            <Typography className={classes.sectionTitle}><PersonOutlineRoundedIcon />Profil du demandeur</Typography>
            <Box className={classes.driverProfile}>
              <Avatar className={classes.avatar} src={photoUrl}><PersonOutlineRoundedIcon /></Avatar>
              <Box className={classes.driverInfo}>
                <Typography className={classes.driverName}>{application?.driverName || "--"}</Typography>
                <Typography className={classes.driverRole}>{application?.applicantCode || "--"}</Typography>
                <Typography className={classes.contactLine}><LocalPhoneOutlinedIcon />{application?.driverPhone || "--"}</Typography>
                <Typography className={classes.contactLine}><MailOutlineRoundedIcon />{application?.driverEmail || "--"}</Typography>
              </Box>
            </Box>
          </Box>

          <Box className={classes.card}>
            <Typography className={classes.sectionTitle}><ShieldOutlinedIcon />Vérification de capacité de remboursement</Typography>
            <Box className={classes.capacityRow}><span><PaymentsOutlinedIcon />Revenus mensuels</span><b>{formatAmount(monthlyIncome)}</b></Box>
            <Box className={classes.capacityRow}><span><PaymentsOutlinedIcon />Charges mensuelles (dossiers + déclarées)</span><b>{formatAmount(eligibility?.monthly_charges)}</b></Box>
            <Box className={classes.capacityRow}><span><PaymentsOutlinedIcon />dont charges déclarées</span><b>{formatAmount(eligibility?.declared_monthly_charges ?? application?.declaredCharges)}</b></Box>
            <Box className={classes.capacityRow}>
              <span><b className={classes.percentIcon}>%</b>{debtCheck?.label || "(Mensualité + charges) / revenu"}</span>
              <b>{debtCheck?.value == null ? "--" : `${Number(debtCheck.value).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`}</b>
            </Box>
            {debtCheck && !debtCheck.ok ? (
              <>
                <Box className={classes.alert}><WarningAmberRoundedIcon /><span><b>À vérifier</b> — seuil {debtCheck.threshold} %</span></Box>
                <Typography className={classes.helpText}>Une validation manuelle est requise avant de poursuivre.</Typography>
              </>
            ) : debtCheck ? (
              <Box className={classes.okAlert}><CheckCircleRoundedIcon /><span><b>Conforme</b> — seuil {debtCheck.threshold} %</span></Box>
            ) : (
              <Typography className={classes.helpText}>Contrôle d'endettement non disponible pour ce demandeur.</Typography>
            )}
          </Box>

          {!creation && (
            <Box className={classes.card}>
              <Typography className={classes.sectionTitle}><AccountBalanceOutlinedIcon />Mise en place Fineract</Typography>
              {FINERACT_STEPS.map((step) => {
                const done = step.reached ? financingReached(status, step.reached) : Boolean(financing?.setup_done);
                return (
                  <Box className={classes.capacityRow} key={step.action}>
                    <span>{done ? <CheckCircleRoundedIcon className={classes.doneIcon} /> : <span className={classes.todoDot} />}{step.label}</span>
                    <b className={done ? classes.doneText : classes.todoText}>{running === step.label ? "En cours…" : done ? "Fait" : "À faire"}</b>
                  </Box>
                );
              })}
            </Box>
          )}

          {!creation && DELIVERY_EDITABLE.includes(status) && (
            <Box className={classes.card}>
              <Typography className={classes.sectionTitle}><LocalShippingOutlinedIcon />Livraison prévue</Typography>
              <Box className={classes.deliveryForm}>
                <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={plannedDate} disabled={!isFinance} error={Boolean(errors.planned_delivery_date)} helperText={errors.planned_delivery_date} onChange={(event) => setPlannedDate(event.target.value)} />
                <TextField select size="small" label="Hub" value={plannedLocation} disabled={!isFinance} error={Boolean(errors.delivery_location_id)} helperText={errors.delivery_location_id} onChange={(event) => setPlannedLocation(event.target.value)}>
                  <MenuItem value="">Non défini</MenuItem>
                  {locations.map((location) => <MenuItem key={location.id} value={location.id}>{[location.name || location.code, location.city].filter(Boolean).join(" · ")}</MenuItem>)}
                </TextField>
              </Box>
              {isFinance && (
                <Button className={classes.linkButton} disabled={Boolean(running)} onClick={handleSaveDelivery}>
                  {running === "Livraison prévue" ? "Enregistrement…" : "Enregistrer la livraison prévue"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {!creation && financing && (
        <Box className={`${classes.card} ${classes.scheduleCard}`}>
          <Typography className={classes.sectionTitle}><FolderOpenOutlinedIcon />Détail du dossier</Typography>
          <DossierDetails financing={financing} notify={notify} onReload={onReload} />
        </Box>
      )}

      <Box className={`${classes.card} ${classes.scheduleCard}`}>
        <Typography className={classes.sectionTitle}><EventAvailableRoundedIcon />Échéancier prévisionnel</Typography>
        <Box className={classes.tableWrap}>
          <table className={classes.scheduleTable}>
            <thead><tr><th>Échéance</th><th>Capital</th><th>Intérêts</th><th>{weekly ? "Montant" : "Mensualité"}</th><th>Solde restant</th></tr></thead>
            <tbody>
              {visiblePeriods.length ? visiblePeriods.map((period) => (
                <tr key={period.period}>
                  <td>{formatDate(period.dueDate)}</td>
                  <td>{formatAmount(period.principal)}</td>
                  <td>{formatAmount(period.interest)}</td>
                  <td>{formatAmount(period.total)}</td>
                  <td>{formatAmount(period.balanceAfter)}</td>
                </tr>
              )) : <tr><td colSpan={5}>Aucune échéance.</td></tr>}
            </tbody>
          </table>
        </Box>
        {periods.length > 3 && (
          <Button className={classes.linkButton} onClick={() => setShowAll((value) => !value)}>
            {showAll ? "Afficher moins" : `Voir les ${periods.length} échéances`}
          </Button>
        )}
      </Box>

      <Box className={classes.actions}>
        <Button className={classes.backButton} startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Retour au dossier</Button>
        <span className={classes.actionSpacer} />
        {creation ? (
          isFinance && (
            <Button className={classes.validateButton} endIcon={<ArrowForwardRoundedIcon />} disabled={Boolean(running) || missingSimulationAmount || Object.keys(clientErrors).length > 0} onClick={handleCreate}>
              {running || "Ouvrir le dossier"}
            </Button>
          )
        ) : (
          <>
            {isFinance && !cancelled && !financingReached(status, "ACTIVE") && !editing && (
              <Button className={classes.draftButton} disabled={Boolean(running)} onClick={() => setCancelOpen(true)}>Annuler le dossier</Button>
            )}
            {isFinance && status === "SUBMITTED" && (editing ? (
              <>
                <Button className={classes.draftButton} disabled={Boolean(running)} onClick={() => { setEditing(false); setServerErrors({}); setForm(formFrom(financing, product, repaymentCount(financing, application))); }}>Annuler la modification</Button>
                <Button className={classes.validateButton} disabled={Boolean(running) || Object.keys(clientErrors).length > 0} onClick={handleSaveConditions}>
                  {running === "Enregistrement" ? "Enregistrement…" : "Enregistrer les conditions"}
                </Button>
              </>
            ) : (
              <Button className={classes.draftButton} disabled={Boolean(running)} onClick={() => setEditing(true)}>Modifier les conditions</Button>
            ))}
            {!editing && (loanApproved || isFinance) && (
              <Button className={classes.validateButton} endIcon={<ArrowForwardRoundedIcon />} disabled={Boolean(running) || cancelled} onClick={handleValidate}>
                {running && running !== "Livraison prévue" ? `${running}…` : loanApproved ? "Continuer vers les contrats" : "Valider le financement"}
              </Button>
            )}
          </>
        )}
      </Box>

      <Dialog open={cancelOpen} onClose={() => !running && setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle className={classes.dialogTitle}>Annuler le dossier ?</DialogTitle>
        <DialogContent>
          <Typography className={classes.helpText}>Le véhicule réservé est remis en vente et les contrats non signés sont annulés.</Typography>
          <TextField fullWidth required multiline minRows={2} size="small" label="Motif" value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button className={classes.backButton} onClick={() => setCancelOpen(false)}>Retour</Button>
          <Button className={classes.dangerButton} disabled={!cancelReason.trim() || Boolean(running)} onClick={handleCancel}>Annuler le dossier</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgrammeMPlusFinancing;
